import React, { useEffect, useState, createRef, forwardRef, memo } from "react";
import axios from "axios";
import { Checkbox, Icon, notification } from "antd";
import CreateSignature from "./CreateSignature";
import { useParams } from "react-router-dom";
import { BASE_SCOPE_API_V2 } from "../../../../dataProvider/env.config"
import { JOB_SIGNOFF_SELECTED_TASKS_URL, JOB_TASK_SIGNOFF_TASKS_URL } from "../../../../dataProvider/constant"
import moment from "moment";
import styles from "./SignOffStyles.module.css";
import ClientSignDetailsForm from "./ClientSignDetailsForm";
import { submit } from 'redux-form';
import { useDispatch } from "react-redux";
import { capitalizeFirst } from "../../../../utils/common";


export default function ClientSignOff(props) {
  const canvasRef = createRef(null);

  const SignatureComponent = forwardRef((props, ref) => <CreateSignature forwardedRef={ref} {...props} />)
  const MemoizedSignature = memo(SignatureComponent)

  return <SignOffPage canvasRef={canvasRef}>
    <div className={`${styles.BlueText} mx-2 pt-2`}>Signature</div>
    <MemoizedSignature width="400" height="200" ref={canvasRef} />
  </SignOffPage>
}

const SignOffPage = (props) => {

  const dispatch = useDispatch();

  const { jobId, signOffCode, orgId } = useParams();

  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("There was an error while loading this page. Please try again.");
  const [completed, setCompleted] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [clientDetails, setClientDetails] = useState({});
  const [jobDetails, setJobDetails] = useState({});
  const [orgDetails, setOrgDetails] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [selectedTasks, setSelectedTasks] = useState([]);

  const submitForm = (values) => {
    var canvas = props.canvasRef.current;
    const context = canvas.getContext('2d');
    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );
    if (selectedTasks.length === 0) {
      notification.warning({
        message: "Please select the tasks for Sign Off.",
        className: 'ant-warning'
      })
    } else if (!pixelBuffer.some(color => color !== 0)) {
      notification.warning({
        message: "Signature is Required.",
        className: 'ant-warning'
      })
    } else {
      setSubmitting(true);
      var dataURL = canvas.toDataURL("image/png");
      let data = { ...values, person_name: capitalizeFirst(values.person_name, true, true), person_role: capitalizeFirst(values.person_role, true, true) }
      data.client_sign_data = dataURL;
      data.job_sign_off_date = moment(values.job_sign_off_date).format("YYYY-MM-DD");
      data.job_id = jobId;
      data.job_label = jobDetails.job_label;
      data.selected_tasks = selectedTasks.map(task => (
        {
          ...(task.sign_off_id && { id: task.sign_off_id }),
          task_label: task.task_label,
          task_id: task.quote_task_id ? task.quote_task_id : 0,
          job_board_task_state: task.job_board_task_state ? task.job_board_task_state : null
        })
      );
      data.sign_off_code = signOffCode;
      data.site_id = jobDetails.site_id;
      data.client_id = clientDetails.client_id;
      data.client_person_id = clientDetails.client_person_id;
      data.acc_manager_user_name = jobDetails.acc_manager_user_name;
      axios({
        method: 'POST',
        url: `${BASE_SCOPE_API_V2}${JOB_TASK_SIGNOFF_TASKS_URL}`,
        headers: {
          Accept: "application/json",
          'Content-Type': 'application/json',
          'org_id': orgId
        },
        data: data
      }).then(() => {
        setCompleted(true);
      }).catch(err => {
        if (err.response) {
          notification.error({
            message: "There was an error while submitting your request.",
            className: "ant-error"
          })
        } else {
          notification.error({
            message: "Please check your connection and try again.",
            className: "ant-error"
          })
        }
      }).finally(() => {
        setSubmitting(false);
      })
    }
  }

  useEffect(() => {
    if (jobId && signOffCode) {
      axios({
        method: 'GET',
        url: `${BASE_SCOPE_API_V2}${JOB_SIGNOFF_SELECTED_TASKS_URL}?job_id=${jobId}&sign_off_code=${signOffCode}`,
        headers: {
          Accept: "application/json",
          'Content-Type': 'application/json',
          'org_id': orgId
        }
      }).then(res => {
        let data = res.data.data;
        if (data.clientDetails && data.clientDetails.length > 0) {
          setClientDetails(data.clientDetails[0])
        }
        if (data.jobDetails) {
          setJobDetails(data.jobDetails);
        }
        if (data.job_sign_off_tasks && data.job_sign_off_tasks.length > 0) {
          setTasks(data.job_sign_off_tasks);
        }
        if (data.orgDetails) {
          setOrgDetails(data.orgDetails)
        }
        if (data.approved_status && data.approved_status === 1) {
          setErrorMessage("Sorry, this link has expired.");
          setHasError(true);
        }
      }).catch(err => {
        if (!err.response) {
          setErrorMessage("Please check your connection and try again.")
        }
        setHasError(true);
      }).finally(() => {
        setLoading(false);
      })
    } else {
      setHasError(true);
      setLoading(false);
    }
  }, []);

  const onTaskCheckChange = ((value, task) => {
    if (value === true) {
      setSelectedTasks([...selectedTasks, task]);
    } else {
      let mTasks = selectedTasks.filter(t => t.task_label !== task.task_label);
      setSelectedTasks([...mTasks]);
    }
  })

  const SubmitButton = () => (
    <button onClick={() => dispatch(submit('clientSignDetailsForm'))} className={styles.GreenSubmitButton}>Approve Sign Off</button>
  )

  return <div className={styles.clientSignOffContainer}>
    {loading ?
      <div className="d-flex align-items-center justify-content-center vh-100">
        <Icon type="loading" style={{ fontSize: "55px", color: '#337ab7' }} />
      </div>
      : hasError ?
        <div className="d-flex align-items-center justify-content-center vh-100">
          <div className="text-center text-danger h5">{errorMessage}</div>
        </div> :
        completed ?
          <div className="d-flex align-items-center justify-content-center vh-100">
            <div className={`text-center h5 ${styles.BlueText}`}>
              Thank you for completing this Sign Off.<br />
              We appreciate the time you took to share your feedback.
            </div>
          </div> :
          <div className="my-3 mb-5">
            <div className="container">
              <div className="row">
                <div className="col py-0 px-0">
                  {submitting &&
                    <div className={styles.overlayBox}>
                      <div className="d-flex align-items-center justify-content-center vh-100">
                        <Icon type="loading" style={{ fontSize: "55px", color: '#337ab7' }} />
                      </div>
                    </div>
                  }
                  <div className="container-fluid">
                    <div className="row align-items-center">
                      <div className="col-3 px-0">
                        <img className="img-fluid" alt="Logo" src={orgDetails && orgDetails.logo ? orgDetails.logo : ''} />
                      </div>
                      <div className="col-9">
                        <div className="header-box">
                          <div className="d-flex align-items-center justify-content-end">
                            <svg width="14" height="14" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg"> <g clipPath="url(#clip0)"> <rect width="512" height="512" fill="white" /> <path d="M512.964 405.007C512.964 411.552 511.752 420.097 509.328 430.643C506.904 441.188 504.358 449.491 501.692 455.552C496.601 467.673 481.813 480.522 457.328 494.097C434.54 506.461 411.995 512.643 389.692 512.643C383.146 512.643 376.783 512.219 370.601 511.37C364.419 510.522 357.449 509.007 349.692 506.825C341.934 504.643 336.176 502.885 332.419 501.552C328.661 500.219 321.934 497.734 312.237 494.097C302.54 490.461 296.601 488.279 294.419 487.552C270.661 479.067 249.449 469.007 230.783 457.37C199.752 438.219 167.692 412.097 134.601 379.007C101.51 345.916 75.3886 313.855 56.2371 282.825C44.6007 264.158 34.5401 242.946 26.0553 219.188C25.328 217.007 23.1462 211.067 19.5098 201.37C15.8734 191.673 13.3886 184.946 12.0553 181.188C10.7219 177.431 8.96436 171.673 6.78254 163.916C4.60072 156.158 3.08557 149.188 2.23708 143.007C1.3886 136.825 0.964355 130.461 0.964355 123.916C0.964355 101.613 7.14617 79.0671 19.5098 56.2792C33.0856 31.7944 45.9341 17.0065 58.0553 11.9156C64.1159 9.24891 72.4189 6.70346 82.9644 4.27922C93.5098 1.85497 102.055 0.642853 108.601 0.642853C111.995 0.642853 114.54 1.00649 116.237 1.73376C120.601 3.18831 127.025 12.4004 135.51 29.3701C138.176 33.9762 141.813 40.5216 146.419 49.0065C151.025 57.4913 155.267 65.1883 159.146 72.0974C163.025 79.0065 166.783 85.4913 170.419 91.552C171.146 92.5217 173.267 95.552 176.783 100.643C180.298 105.734 182.904 110.037 184.601 113.552C186.298 117.067 187.146 120.522 187.146 123.916C187.146 128.764 183.692 134.825 176.783 142.097C169.873 149.37 162.358 156.037 154.237 162.097C146.116 168.158 138.601 174.582 131.692 181.37C124.783 188.158 121.328 193.734 121.328 198.097C121.328 200.279 121.934 203.007 123.146 206.279C124.358 209.552 125.389 212.037 126.237 213.734C127.086 215.431 128.783 218.34 131.328 222.461C133.873 226.582 135.267 228.885 135.51 229.37C153.934 262.582 175.025 291.067 198.783 314.825C222.54 338.582 251.025 359.673 284.237 378.097C284.722 378.34 287.025 379.734 291.146 382.279C295.267 384.825 298.176 386.522 299.873 387.37C301.57 388.219 304.055 389.249 307.328 390.461C310.601 391.673 313.328 392.279 315.51 392.279C319.873 392.279 325.449 388.825 332.237 381.916C339.025 375.007 345.449 367.491 351.51 359.37C357.57 351.249 364.237 343.734 371.51 336.825C378.783 329.916 384.843 326.461 389.692 326.461C393.086 326.461 396.54 327.31 400.055 329.007C403.57 330.703 407.873 333.31 412.964 336.825C418.055 340.34 421.086 342.461 422.055 343.188C428.116 346.825 434.601 350.582 441.51 354.461C448.419 358.34 456.116 362.582 464.601 367.188C473.086 371.794 479.631 375.431 484.237 378.097C501.207 386.582 510.419 393.007 511.873 397.37C512.601 399.067 512.964 401.613 512.964 405.007Z" fill="#808080" /> </g> <defs> <clipPath id="clip0"> <rect width="512" height="512" fill="white" /> </clipPath> </defs> </svg>
                            <div className="pl-2">{orgDetails && orgDetails.phone_number ? orgDetails.phone_number : ''}</div>
                          </div>
                          <div className="d-flex align-items-center justify-content-end">
                            <svg width="14" height="14" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="512" height="512" fill="white" /><path fillRule="evenodd" clipRule="evenodd" d="M256 0C115.2 0 0 115.2 0 256C0 396.8 115.2 512 256 512C396.8 512 512 396.8 512 256C512 115.2 396.8 0 256 0ZM432.64 153.6H358.4C350.72 120.32 337.92 92.16 322.56 61.44C368.64 79.36 409.6 110.08 432.64 153.6ZM256 51.2C276.48 81.92 294.4 115.2 304.64 153.6H207.36C217.6 117.76 235.52 81.92 256 51.2ZM58.88 307.2C53.76 291.84 51.2 273.92 51.2 256C51.2 238.08 53.76 220.16 58.88 204.8H145.92C143.36 222.72 143.36 238.08 143.36 256C143.36 273.92 145.92 289.28 145.92 307.2H58.88ZM79.36 358.4H153.6C161.28 391.68 174.08 419.84 189.44 450.56C143.36 432.64 102.4 401.92 79.36 358.4ZM153.6 153.6H79.36C104.96 110.08 143.36 79.36 189.44 61.44C174.08 92.16 161.28 120.32 153.6 153.6ZM256 460.8C235.52 430.08 217.6 396.8 207.36 358.4H304.64C294.4 394.24 276.48 430.08 256 460.8ZM314.88 307.2H197.12C194.56 289.28 192 273.92 192 256C192 238.08 194.56 222.72 197.12 204.8H317.44C320 222.72 322.56 238.08 322.56 256C322.56 273.92 317.44 289.28 314.88 307.2ZM322.56 450.56C337.92 422.4 350.72 391.68 358.4 358.4H432.64C409.6 401.92 368.64 432.64 322.56 450.56ZM368.64 307.2C371.2 289.28 371.2 273.92 371.2 256C371.2 238.08 368.64 222.72 368.64 204.8H455.68C460.8 220.16 463.36 238.08 463.36 256C463.36 273.92 460.8 291.84 455.68 307.2H368.64Z" fill="#808080" /></svg>
                            <div className="pl-2">{orgDetails && orgDetails.website ? orgDetails.website : ''}</div>
                          </div>
                          <div className="d-flex align-items-center justify-content-end">
                            <svg width="14" height="14" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg"><g clipPath="url(#clip0)"><rect width="512" height="512" fill="white" /><path d="M8.52686 133.392C12.0298 116.354 21.2999 101.043 34.7747 90.0425C48.2494 79.0416 65.1048 73.0226 82.4999 73H443.5C461.29 73.0392 478.494 79.3628 492.074 90.8543C505.654 102.346 514.738 118.266 517.72 135.804L263.08 236.169L8.52686 133.392ZM269.705 272.863C265.348 274.583 260.496 274.564 256.152 272.808L7 172.215V363.214C7.02178 383.232 14.9832 402.423 29.1375 416.577C43.2917 430.731 62.4828 438.693 82.4999 438.714H443.5C463.517 438.693 482.708 430.731 496.863 416.577C511.017 402.423 518.978 383.232 519 363.214V174.599L269.705 272.863Z" fill="#808080" /></g><defs><clipPath id="clip0"><rect width="512" height="512" fill="white" /></clipPath></defs></svg>
                            <div className="pl-2">{orgDetails && orgDetails.email_address ? orgDetails.email_address : ''}</div>
                          </div>
                          <div className="d-flex justify-content-end">
                            <svg width="18" height="18" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="512" height="512" fill="white" /><g clipPath="url(#clip0)"><path d="M249.001 63.6667C166.548 63.6667 99.668 130.547 99.668 213C99.668 325 249.001 490.333 249.001 490.333C249.001 490.333 398.335 325 398.335 213C398.335 130.547 331.455 63.6667 249.001 63.6667ZM249.001 266.333C219.561 266.333 195.668 242.44 195.668 213C195.668 183.56 219.561 159.667 249.001 159.667C278.441 159.667 302.335 183.56 302.335 213C302.335 242.44 278.441 266.333 249.001 266.333Z" fill="#808080" /></g><defs><clipPath id="clip0"><rect width="512" height="512" fill="white" /></clipPath></defs></svg>
                            <div className="doc-address">
                              <div className="pl-2 text-right">{orgDetails && orgDetails.address ? orgDetails.address : ''}</div>
                            </div>
                          </div>
                          <div className="d-flex align-items-top justify-content-end">
                            <div className="pl-2">ABN: {orgDetails && orgDetails.abn_acn ? orgDetails.abn_acn : ''}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="doc-header-seperator my-2"></div>
                  <div className="container-fluid">
                    <div className="row">
                      <div className="col">
                        <div className={styles.PageTitle}>Sign Off Sheet</div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6">
                        <div>
                          <div>
                            <span className={styles.BlueText}>Attention:</span> {clientDetails && clientDetails.client_person_name ? clientDetails.client_person_name : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Company:</span> {clientDetails && clientDetails.client_name ? clientDetails.client_name : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Address:</span> {clientDetails && clientDetails.client_address ? clientDetails.client_address : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Mobile:</span> {clientDetails && clientDetails.client_phone ? clientDetails.client_phone : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Email:</span> {clientDetails && clientDetails.client_email ? clientDetails.client_email : ''}
                          </div>
                        </div>
                      </div>
                      <div className="col-6 text-right">
                        <div>
                          <div>
                            <span className={`${styles.BlueText} font-weight-bold`}>Job Details</span>
                          </div>
                          <div>
                            <span className={styles.BlueText}>Job ID:</span> {jobDetails && jobDetails.job_label ? jobDetails.job_label : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Job Manager:</span> {jobDetails && jobDetails.job_manager ? jobDetails.job_manager : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Mobile:</span> {jobDetails && jobDetails.mobile ? jobDetails.mobile : ''}
                          </div>
                          <div>
                            <span className={styles.BlueText}>Email:</span> {jobDetails && jobDetails.email ? jobDetails.email : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="container-fluid">
                    <div className="row">
                      <div className="col">
                        <div className="table-borderless border my-3" style={{ overflowX: "auto" }}>
                          <table className="custom-signoff-table table">
                            <thead>
                              <tr>
                                <th colspan="5">
                                  <div className="pl-3">
                                    Job Location: <span className="text_black">{jobDetails && jobDetails.site_address ? jobDetails.site_address : ''}</span>
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody className="table-body">
                              {tasks.map(task =>
                                <tr key={task.task_label}>
                                  <td className="text-center" width="70">
                                    <Checkbox
                                      onChange={(e) => onTaskCheckChange(e.target.checked, task)}
                                      checked={selectedTasks.filter(t => t.task_label === task.task_label).length === 1} />
                                  </td>
                                  <td style={{ minWidth: "180px" }}>
                                    <div className={styles.BlueText}>{task.task_label ? task.task_label : ''}</div>
                                    <div>{task.task_name ? task.task_name : ''}</div>
                                    <div>{task.area_name ? task.area_name : ''}</div>
                                    <div>{task.task_note ? task.task_note : ''}</div>
                                  </td>
                                  <td className="text-center" width="160"><div>{task.task_state ? task.task_state : ''}</div></td>
                                  <td style={{ minWidth:"160px" }}>
                                    <div>
                                      {task.person_name && <div><span className={styles.BlueText}>Name :</span> {task.person_name}</div>}
                                      {task.person_role && <div><span className={styles.BlueText}>Role :</span> {task.person_role}</div>}
                                      {task.job_sign_off_date && <div><span className={styles.BlueText}>Date :</span> {moment(task.job_sign_off_date).format("DD-MM-YYYY")}</div>}
                                    </div>
                                  </td>
                                  <td width="120" className="text-center">{task.client_sign ? <img alt="signature" src={task.client_sign} style={{ maxWidth: '100px' }} /> : ''}</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="container-fluid">
                    <div className="row">
                      <div className="col">
                        <div className="container-fluid">
                          <ClientSignDetailsForm onSubmit={submitForm}>
                            {props.children}
                          </ClientSignDetailsForm>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col py-3">
                        <div className="text-right">
                          <SubmitButton />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <div className={`${styles.GreyBox} border`}>
                          <div className={`${styles.BlueText} ${styles.TermsTitle} font-weight-bold`}>Terms &amp; Conditions</div>
                          <div className="px-3">
                            <ol className="signOff-conditions-list">
                              <li>Signing off on this document signifies that you (approving client representative) is satisfied with the completed deliverables listed above.</li>
                              <li>FAMASO cannot provide any warranties on the completed deliverables beyond 24 hours of completion.</li>
                              <li>If after this period we do not receive the completed Sign Off, FAMASO will conclude that the Job has been signed off by you and no further action will be required.</li>
                            </ol>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
    }
  </div>
}
